export const validatePostalCode = (postalCode) => {
  const postalCodeRegex = /^\d{5}$/;
  if (!postalCode || postalCode.trim() === "") {
    return { isValid: false, error: "Postleitzahl muss ausgefüllt werden" };
  }
  if (!postalCodeRegex.test(postalCode.trim())) {
    return { isValid: false, error: "Postleitzahl muss genau 5 Ziffern enthalten" };
  }
  return { isValid: true, error: "" };
};

/**
 * Validates city name (must not contain numbers)
 */
export const validateCity = (city) => {
  const cityRegex = /^[a-zA-ZäöüÄÖÜß\s\-]+$/;
  if (!city || city.trim() === "") {
    return { isValid: false, error: "Stadt/Ort muss ausgefüllt werden" };
  }
  if (!cityRegex.test(city.trim())) {
    return { isValid: false, error: "Stadt/Ort darf keine Zahlen enthalten" };
  }
  return { isValid: true, error: "" };
};

/**
 * Validates street (must contain both letters and numbers)
 */
export const validateStreet = (street) => {
  const hasLetters = /[a-zA-ZäöüÄÖÜß]/.test(street);
  const hasNumbers = /\d/.test(street);
  
  if (!street || street.trim() === "") {
    return { isValid: false, error: "Straße und Hausnummer muss ausgefüllt werden" };
  }
  if (!hasLetters) {
    return { isValid: false, error: "Straße muss Buchstaben enthalten" };
  }
  if (!hasNumbers) {
    return { isValid: false, error: "Bitte Hausnummer angeben" };
  }
  return { isValid: true, error: "" };
};

/**
 * Validates country
 */
export const validateCountry = (country) => {
  if (!country || country.trim() === "") {
    return { isValid: false, error: "Bitte wähle ein Land aus." };
  }
  return { isValid: true, error: "" };
};

export const createAddressValidationKey = ({ street, postalCode, city, country }) =>
  [street, postalCode, city, country]
    .map((value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' '))
    .join('|');

/**
 * Validates address using Google Geocoding API
 * @param {Object} addressData - Object containing street, postalCode, city, country
 * @returns {Promise<{isValid: boolean, error: string, coordinates?: {lat: number, lng: number}}>}
 */
export const validateAddressWithGoogle = async (addressData) => {
  const { street, postalCode, city, country } = addressData;
  
  // First perform local validation
  const streetValidation = validateStreet(street);
  if (!streetValidation.isValid) {
    return streetValidation;
  }
  
  const postalCodeValidation = validatePostalCode(postalCode);
  if (!postalCodeValidation.isValid) {
    return postalCodeValidation;
  }
  
  const cityValidation = validateCity(city);
  if (!cityValidation.isValid) {
    return cityValidation;
  }
  
  const countryValidation = validateCountry(country);
  if (!countryValidation.isValid) {
    return countryValidation;
  }
  
  // Construct full address
  const fullAddress = `${street}, ${postalCode} ${city}, ${country}`;
  
  try {
    const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
    
    if (!apiKey) {
      console.error('Google API key not found');
      return { 
        isValid: false, 
        error: "Adressvalidierung nicht verfügbar. Bitte versuche es später erneut." 
      };
    }

    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(fullAddress)}&key=${apiKey}`
    );

    
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    
    const data = await response.json();
    
    if (data.status === 'ZERO_RESULTS') {
      return { 
        isValid: false, 
        error: "Diese Adresse konnte nicht gefunden werden. Bitte überprüfe deine Eingabe." 
      };
    }
    
    if (data.status === 'REQUEST_DENIED') {
      return { 
        isValid: false, 
        error: "Adressvalidierung nicht möglich. Bitte versuche es später erneut." 
      };
    }
    
    if (data.status !== 'OK' || !data.results || data.results.length === 0) {
      return { 
        isValid: false, 
        error: "Diese Adresse konnte nicht gefunden werden. Bitte überprüfe deine Eingabe." 
      };
    }
    
    // Check if the result is in Germany and matches the postal code
    const result = data.results[0];
    
    // CRITICAL: Reject partial matches - Google is being too "helpful"
    if (result.partial_match === true) {
      return { 
        isValid: false, 
        error: "Die angegebene Adresse ist nicht vollständig korrekt." 
      };
    }
    
    const addressComponents = result.address_components;
    
    // Verify country
    const countryComponent = addressComponents.find(
      comp => comp.types.includes('country')
    );
    if (!countryComponent || countryComponent.short_name !== 'DE') {
      return { 
        isValid: false, 
        error: "Die Adresse muss in Deutschland liegen." 
      };
    }
    
    // Verify postal code
    const postalCodeComponent = addressComponents.find(
      comp => comp.types.includes('postal_code')
    );
    if (!postalCodeComponent || postalCodeComponent.long_name !== postalCode.trim()) {
      return { 
        isValid: false, 
        error: "Die Postleitzahl stimmt nicht mit der angegebenen Adresse überein." 
      };
    }
    
    // Verify city/locality - must match what user entered
    const cityComponent = addressComponents.find(
      comp => comp.types.includes('locality') || comp.types.includes('postal_town')
    );
    if (!cityComponent) {
      return { 
        isValid: false, 
        error: "Die Stadt konnte nicht verifiziert werden. Bitte überprüfe deine Eingabe." 
      };
    }
    
    // Check if the city name matches (case-insensitive comparison with umlaut normalization)
    const normalizeString = (str) => {
      return str
        .toLowerCase()
        .trim()
        .replace(/\s+/g, ' ')
        // Normalize German umlauts to their ASCII equivalents
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .replace(/ß/g, 'ss');
    };
    
    const googleCity = normalizeString(cityComponent.long_name);
    const userCity = normalizeString(city);
    
    if (googleCity !== userCity) {
      return { 
        isValid: false, 
        error: `Die Stadt stimmt nicht überein. Google fand "${cityComponent.long_name}" statt "${city}".` 
      };
    }
    
    // Verify that street number exists in the address
    const streetNumberComponent = addressComponents.find(
      comp => comp.types.includes('street_number')
    );
    
    // Extract number from user's street input
    const streetNumberMatch = street.match(/\d+/);
    const userStreetNumber = streetNumberMatch ? streetNumberMatch[0] : null;
    
    if (userStreetNumber && !streetNumberComponent) {
      return { 
        isValid: false, 
        error: "Die Hausnummer konnte nicht verifiziert werden. Bitte überprüfe die Adresse." 
      };
    }
    
    // Get coordinates
    const coordinates = {
      lat: result.geometry.location.lat,
      lng: result.geometry.location.lng
    };
    
    // Extract normalized street name from Google's response
    const routeComponent = addressComponents.find(
      comp => comp.types.includes('route')
    );
    
    // Build normalized street: route name + street number (if available)
    // Note: streetNumberComponent was already declared above for validation
    let normalizedStreet = '';
    if (routeComponent) {
      normalizedStreet = routeComponent.long_name;
      if (streetNumberComponent) {
        normalizedStreet = `${normalizedStreet} ${streetNumberComponent.long_name}`;
      }
    } else {
      // Fallback: use user's input if Google doesn't provide route component
      normalizedStreet = street;
    }
    
    // Extract normalized city name from Google's response
    const normalizedCity = cityComponent.long_name;
    
    return { 
      isValid: true, 
      error: "",
      coordinates,
      formattedAddress: result.formatted_address,
      normalizedStreet,
      normalizedCity
    };
    
  } catch (error) {
    console.error('Error validating address with Google:', error);
    return { 
      isValid: false, 
      error: "Fehler bei der Adressüberprüfung. Bitte versuche es erneut." 
    };
  }
};

/**
 * Validates all address fields locally (without API call)
 * @param {Object} addressData - Object containing street, postalCode, city, country
 * @returns {Object} Object with field errors
 */
export const validateAddressFields = (addressData) => {
  const { street, postalCode, city, country } = addressData;
  
  return {
    country: validateCountry(country).error,
    street: validateStreet(street).error,
    postalCode: validatePostalCode(postalCode).error,
    city: validateCity(city).error
  };
};
